import React from 'react';
import { FiX, FiDownload } from 'react-icons/fi';

export interface DocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTitle: string;
  documentUrl?: string;
  isLoading?: boolean;
}

/**
 * Check if URL is an image based on common image extensions or S3 content
 */
const isImageUrl = (url: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
  const lowerUrl = url.toLowerCase();

  // Check for image extensions in URL
  if (imageExtensions.some((ext) => lowerUrl.includes(ext))) {
    return true;
  }

  // S3 URLs for images typically contain these patterns or the title gives hints
  return true; // Assume image for S3 URLs since most documents are images
};

export const DocumentModal: React.FC<DocumentModalProps> = ({
  isOpen,
  onClose,
  documentTitle,
  documentUrl,
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleDownload = () => {
    if (documentUrl) {
      // Abrir em nova aba para download
      window.open(documentUrl, '_blank');
    }
  };

  const shouldShowAsImage = documentUrl ? isImageUrl(documentUrl) : false;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">{documentTitle}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Document Viewer */}
        <div className="flex-1 overflow-auto bg-neutral-100 p-6 flex items-center justify-center">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                <p className="text-neutral-600">Carregando documento...</p>
              </div>
            </div>
          ) : documentUrl ? (
            shouldShowAsImage ? (
              <img
                src={documentUrl}
                alt={documentTitle}
                className="max-w-full max-h-full object-contain rounded-lg shadow-md"
              />
            ) : (
              <iframe
                src={documentUrl}
                title={documentTitle}
                className="w-full h-full border-0 rounded-lg bg-white"
              />
            )
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-6xl mb-4">📄</div>
                <p className="text-neutral-600">Documento não disponível</p>
                <p className="text-sm text-neutral-500 mt-2">
                  Não foi possível carregar o documento
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-neutral-200">
          <button
            type="button"
            onClick={handleDownload}
            disabled={!documentUrl || isLoading}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiDownload className="w-5 h-5" />
            Baixar documento
          </button>
        </div>
      </div>
    </div>
  );
};
